import { flattenConnection } from "@shopify/hydrogen-react";
import type {
  Product,
  MediaConnection,
  MetaobjectConnection,
  Metaobject,
} from "@shopify/hydrogen-react/storefront-api-types";
import { useQuery } from "react-query";
import { client } from "./client";
import { sanitizeShopifyHTML } from "../components/ShopifyRichText";
import { parseGid, prefixGid } from "../utils/shopifyId";
import { productAdminKeys } from "./queryKeys";
import type {
  ProductAdminData,
  ProductStatus,
  VariantAdminNode,
} from "../types";

const PRODUCT_ADMIN_QUERY = `#graphql
  query productAdmin($id: ID!) {
    product(id: $id) {
      id
      description
      descriptionHtml
      handle
      productType
      publishedAt
      tags
      title
      updatedAt
      vendor
      artistId: metafield(namespace: "hug", key: "artist_id") {
        value
      }
      details: metafield(namespace: "hug", key: "product_details") {
        value
        references(first: 10) {
          ... on MetafieldReferenceConnection {
            edges {
              node {
                ...on Metaobject {
                  id
                  fields {
                    key
                    value
                  }
                }
              }
            }
          }
        }
      }
      featuredImage {
        id
        altText
        height
        width
        url
      }
      media(first: 100) {
        nodes {
          id
          mediaContentType
          ...on MediaImage {
            image {
              url
              altText
              height
              width
            }
          }
        }
      }
      options {
        name
        values
      }
      status: metafield(namespace: "hug", key: "status") {
        value
      }
      templateId: metafield(namespace: "hug", key: "template_id") {
        value
      }
      assetUrl: metafield(namespace: "hug", key: "asset_url") {
        value
      }
      variants(first: 100) {
        nodes {
          id
          sku
          title
          image {
            id
            url
            altText
            width
            height
          }
          price {
            currencyCode
            amount
          }
          selectedOptions {
            name
            value
          }
          templateVariantId: metafield(namespace: "hug", key: "template_variant_id") {
            value
          }
        }
      }
    }
  }
`;

type ProductAdminResp = Pick<
  Product,
  | "id"
  | "description"
  | "descriptionHtml"
  | "handle"
  | "productType"
  | "publishedAt"
  | "tags"
  | "title"
  | "updatedAt"
  | "vendor"
  | "featuredImage"
  | "options"
> & {
  artistId?: { value: string };
  status?: { value: ProductStatus };
  templateId?: { value: string };
  assetUrl?: { value: string };
  details?: {
    value: string;
    references: Partial<MetaobjectConnection>;
  };
  variants: {
    nodes: VariantAdminNode[];
  };
  media: MediaConnection;
};

const getProductAdmin = (id?: string | null) =>
  id
    ? client.query<{ product?: ProductAdminResp }>(PRODUCT_ADMIN_QUERY, {
        id: prefixGid(id),
      })
    : Promise.reject(new Error("Invalid product id"));

/**
 * Fetch Product by id for managing as shop admin
 */
const useProductAdminQuery = (id?: string | null) =>
  useQuery<
    { product?: ProductAdminResp },
    unknown,
    ProductAdminData | undefined
  >(productAdminKeys.detail(id), () => getProductAdmin(id), {
    enabled: !!id,
    select: ({ product }) => {
      if (!product) {
        return undefined;
      }

      const details = !product.details?.references
        ? []
        : flattenConnection(product.details.references).filter(
            (d): d is Metaobject => !!d,
          );
      const media = !product.media ? [] : flattenConnection(product.media);
      const variants = !product.variants
        ? []
        : flattenConnection(product.variants).map((v) => ({
            ...v,
            templateVariantId: v.templateVariantId?.value,
          }));

      return {
        ...product,
        id: parseGid(product.id).id,
        gid: product.id,
        artistId: product.artistId?.value,
        assetUrl: product.assetUrl?.value,
        descriptionHtml: sanitizeShopifyHTML(product.descriptionHtml),
        details,
        media,
        status: product.status?.value,
        templateId: product.templateId?.value,
        variants,
      };
    },
  });

export { useProductAdminQuery };
